import { useApolloClient } from '@apollo/client';
import {
  Details,
  type DetailsProps,
  createUpdateGQLFragmentGenerator,
  generateArrayMutationsWithUpdates,
} from '@axinom/mosaic-ui';
import gql from 'graphql-tag';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
  PLACEHOLDER_BUILT_IN_AZURE_AD_IDP_CLIENT_ID,
  PLACEHOLDER_BUILT_IN_AZURE_AD_IDP_CLIENT_SECRET,
  PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_ID,
  PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_SECRET,
} from '../../../../common/constants/idp-constants';
import {
  IdentityProvider,
  IdpConfigDataDocument,
  type Mutation,
  type MutationCreateIdpConfigurationArgs,
  type MutationDeleteIdpConfigurationArgs,
  type MutationUpdateIdpConfigurationArgs,
  useIdpConfigDataQuery,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import type {
  FormDataIdProvider,
  FormDataInternalIdpConfiguration,
  IdProviderFormData,
} from './IdentityProviders.types';
import { ProviderField } from './ProviderField';

export const IdentityProviders: React.FC = () => {
  const apolloClient = useApolloClient();

  const { envId } = useParams<{
    envId: string;
  }>();

  const { loading, data, error } = useIdpConfigDataQuery({
    client: apolloClient,
    fetchPolicy: 'no-cache',
    variables: {
      condition: {
        environmentId: envId,
      },
    },
  });

  const onSubmit = useCallback(
    async (
      formData: IdProviderFormData,
      initialData: DetailsProps<IdProviderFormData>['initialData'],
    ): Promise<void> => {
      const generateUpdateGQLFragment =
        createUpdateGQLFragmentGenerator<Mutation>();

      const currentData = formData.externalIdpConfiguration;
      if (
        currentData !== undefined &&
        formData.internalIdpConfiguration !== undefined &&
        formData.internalIdpConfiguration.isAvailable !== false
      ) {
        currentData.push(formData.internalIdpConfiguration);
      }

      const initialDataCombined = initialData.data?.externalIdpConfiguration;

      if (
        initialDataCombined !== undefined &&
        initialData.data?.internalIdpConfiguration !== undefined &&
        initialData.data.internalIdpConfiguration.isAvailable !== false
      ) {
        initialDataCombined.push(initialData.data.internalIdpConfiguration);
      }

      const mutations = generateArrayMutationsWithUpdates({
        current: currentData,
        original: initialDataCombined,
        generateCreateMutation: (item: FormDataIdProvider): string => {
          let defaultIdpImageUrl: string;
          let defaultDisplayName: string;
          let discoveryDocumentUrl: string | null = null;
          let clientId: string | null = null;
          let clientSecret: string | null = null;
          let useCustomBranding = false;

          const enabled = item.status === 'ENABLED' ? true : false;

          const scopes =
            data?.scopes?.nodes
              .filter((scope) => scope.idpId === item.idpId)
              .map((scope) => scope.scopeName) ?? [];

          switch (item.idpId) {
            case IdentityProvider.Axinom:
              defaultIdpImageUrl = 'DEFAULT_AXINOM';
              defaultDisplayName = 'Sign In with Axinom';
              discoveryDocumentUrl = 'NOT_USED';
              clientId = 'NOT_USED';
              clientSecret = 'NOT_USED';
              break;
            case IdentityProvider.Google:
              defaultIdpImageUrl = 'DEFAULT_GOOGLE';
              defaultDisplayName = 'Sign In with Google';
              discoveryDocumentUrl =
                'https://accounts.google.com/.well-known/openid-configuration';
              clientId = PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_ID;
              clientSecret = PLACEHOLDER_BUILT_IN_GOOGLE_IDP_CLIENT_SECRET;
              break;
            case IdentityProvider.AzureAd:
              defaultIdpImageUrl = 'DEFAULT_AZURE';
              defaultDisplayName = 'Sign In with Microsoft';
              discoveryDocumentUrl =
                'https://login.microsoftonline.com/organizations/v2.0/.well-known/openid-configuration';
              clientId = PLACEHOLDER_BUILT_IN_AZURE_AD_IDP_CLIENT_ID;
              clientSecret = PLACEHOLDER_BUILT_IN_AZURE_AD_IDP_CLIENT_SECRET;
              break;
            case IdentityProvider.Custom:
              useCustomBranding = true;
              defaultIdpImageUrl = '';
              defaultDisplayName = '';
              break;
            default:
              defaultIdpImageUrl = '';
              defaultDisplayName = '';
              break;
          }

          return generateUpdateGQLFragment<MutationCreateIdpConfigurationArgs>(
            'createIdpConfiguration',
            {
              input: {
                idpConfiguration: {
                  sortOrder: item.sortOrder,
                  idpId: { type: 'enum', value: item.idpId },
                  scopes,
                  environmentId: envId,
                  enabled,
                  idpImageUrl: defaultIdpImageUrl,
                  displayName: defaultDisplayName,
                  discoveryDocumentUrl,
                  useCustomBranding,
                  clientId,
                  clientSecret,
                },
              },
            },
          );
        },
        generateDeleteMutation: (item: FormDataIdProvider): string =>
          generateUpdateGQLFragment<MutationDeleteIdpConfigurationArgs>(
            'deleteIdpConfiguration',
            { input: { id: item.id } },
          ),
        generateUpdateMutation: (item: FormDataIdProvider): string =>
          generateUpdateGQLFragment<MutationUpdateIdpConfigurationArgs>(
            'updateIdpConfiguration',
            {
              input: {
                id: item.id,
                patch: { sortOrder: item.sortOrder },
              },
            },
          ),
        key: 'id',
      });

      const GqlMutationDocument = gql`mutation UpdateIdpConfiguration {
        ${mutations}
      }`;

      await apolloClient.mutate({
        mutation: GqlMutationDocument,
        refetchQueries: [IdpConfigDataDocument],
        awaitRefetchQueries: true,
      });
    },
    [apolloClient, data?.scopes?.nodes, envId],
  );

  const internalIdpConfiguration: FormDataInternalIdpConfiguration = useMemo(
    () =>
      data?.idpConfigurations?.nodes.filter(
        (idpConfig) => idpConfig.idpId === IdentityProvider.AxAuth,
      )[0] ?? {
        id: '',
        idpId: IdentityProvider.AxAuth,
        sortOrder: 0,
        isAvailable: false,
        enabled: false,
        displayName: '',
        useCustomBranding: false,
        environmentId: envId,
      },
    [data, envId],
  );

  return (
    <Details<IdProviderFormData>
      defaultTitle="Identity Providers"
      subtitle="Properties"
      initialData={{
        data: {
          internalIdpConfiguration,
          externalIdpConfiguration:
            data?.idpConfigurations?.nodes.filter(
              (idpConfig) => idpConfig.idpId !== IdentityProvider.AxAuth,
            ) ?? [],
        },
        loading,
        error: error?.message,
      }}
      saveData={onSubmit}
    >
      <ProviderField />
    </Details>
  );
};

export const IdProvidersCrumb: BreadcrumbResolver = () => 'Identity Providers';
